import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import StarIcon from "@mui/icons-material/Star";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints, MEDIA_URL } from "src/network/endpoints";
import { toast } from "react-hot-toast";
import BankAccountDialog from "./BankAccountDialog";
import CashOutDialog from "./CashOutDialog";
import CashOutResultDialog from "./CashOutResultDialog";
import useBusinessPermissions from "src/hooks/useBusinessPermissions";
import ApprovIcon from "src/@core/components/icons/ApprovIcon";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const defaultProfileImage = require("../../assets/images/Default_pfp.jpg");

const WalletSetup = ({ businessLocationData, IsMaxView = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const queryClient = useQueryClient();
  const { hasPermission, isOwnBusiness } = useBusinessPermissions();
  const canManageWallet = hasPermission("manageWallet");
  const isDisabled = !isOwnBusiness && !canManageWallet;
  const [range, setRange] = useState("all-time");
  const [rangeBalance, setRangeBalance] = useState(0);

  // Get default/primary location
  const defaultLocationObj = useMemo(() => {
    const locations = businessLocationData?.locations || [];
    return locations.find((loc) => loc.isPrimaryAddress) || locations[0];
  }, [businessLocationData]);

  // State for selected location
  const [selectedLocationId, setSelectedLocationId] = useState("");

  // Set default location on mount
  useEffect(() => {
    if (defaultLocationObj && !selectedLocationId) {
      setSelectedLocationId(defaultLocationObj._id);
    }
  }, [defaultLocationObj, selectedLocationId]);

  // Use selected location ID or fallback
  const locationId =
    selectedLocationId ||
    defaultLocationObj?._id ||
    businessLocationData?.locations?.[0]?._id ||
    businessLocationData?.location?._id ||
    businessLocationData?._id;

  // Fetch wallet data
  const {
    data: walletData,
    isLoading,
    error,
  } = useQuery(
    ["walletData", locationId],
    async ({ signal }) => {
      if (!locationId) return null;
      const res = await axiosInstance.get(
        ApiEndPoints.WALLET.getByLocation(locationId),
        { signal }
      );
      return res.data.data;
    },
    {
      enabled: !!locationId,
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const getWalletCurrentBalance = async (selectedRange) => {
    try {
      console.log("Calling API for:", selectedRange);

      const response = await axiosInstance.get(
        ApiEndPoints.WALLET.getByCurrentBalance(locationId, selectedRange),
        {
          params: {
            range: selectedRange
          },
        }
      );

      setRangeBalance(response.data.data.currentBalance)
      return response.data;
    } catch (error) {
      console.error("API error:", error);
      throw error;
    }
  };

  useEffect(() => {
    getWalletCurrentBalance(range);
  }, [range]);

  const handleFilterChange = (event) => {
    setRange(event.target.value);
  };

  // Fetch countries
  const { data: countryData } = useQuery(
    ["countries"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.country, { signal });
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
    }
  );

  // Fetch currencies
  const { data: currencyData } = useQuery(
    ["currencies"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.currency, { signal });
      return res.data.data || res.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
    }
  );

  // Fetch bank accounts
  const { data: bankAccountsData, refetch: refetchBankAccounts } = useQuery(
    ["bankAccounts", locationId],
    async ({ signal }) => {
      if (!locationId) return null;
      const res = await axiosInstance.get(
        ApiEndPoints.BANK_ACCOUNT.getByLocation(locationId),
        { signal }
      );
      return res.data.data;
    },
    {
      enabled: !!locationId,
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
    }
  );

  // Fetch last cash out data
  const { data: lastCashOutData } = useQuery(
    ["lastCashOut", locationId],
    async ({ signal }) => {
      if (!locationId) return null;
      const res = await axiosInstance.get(
        ApiEndPoints.WALLET.getLastCashOut(locationId),
        { signal }
      );
      return res.data.data;
    },
    {
      enabled: !!locationId,
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
    }
  );

  // Check if there are any bank accounts
  const hasBankAccounts = (bankAccountsData?.accounts?.length || 0) > 0;

  // Format last cash out date for display
  const formatLastCashOutDate = useCallback((dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    // Get ordinal suffix for day
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  }, []);

  // Get the most recent cash out date
  const lastCashOutDate = useMemo(() => {
    if (!lastCashOutData?.cashoutRequests || lastCashOutData.cashoutRequests.length === 0) {
      return null;
    }
    // Sort by createdAt descending and get the first one
    const sorted = [...lastCashOutData.cashoutRequests].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return formatLastCashOutDate(sorted[0].createdAt);
  }, [lastCashOutData, formatLastCashOutDate]);

  // Get the last cash out request status and reason
  const lastCashOutStatus = useMemo(() => {
    if (!lastCashOutData?.cashoutRequests || lastCashOutData.cashoutRequests.length === 0) {
      return null;
    }
    
    // Sort all requests by createdAt descending to get the most recent one
    const sortedRequests = [...lastCashOutData.cashoutRequests].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    
    const mostRecent = sortedRequests[0];
    
    // If the most recent request is pending, return pending status
    if (mostRecent.status === "pending") {
      return {
        status: "pending",
        reason: null,
      };
    }
    
    // If not pending, return the status and reason of the most recent non-pending request
    return {
      status: mostRecent.status,
      reason: mostRecent.reason || null,
    };
  }, [lastCashOutData]);

  // Check if any cashout request has status "suspend" - if so, disable cashout
  const hasSuspendedCashOut = useMemo(() => {
    if (!lastCashOutData?.cashoutRequests || lastCashOutData.cashoutRequests.length === 0) {
      return false;
    }
    return lastCashOutData.cashoutRequests.some(
      (request) => request.status === "suspend"
    );
  }, [lastCashOutData]);

  // Format currency
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount / 100); // Amount is in cents (e.g., 50 = $0.50)
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };


  // Get profile image URL
  const getProfileImageUrl = (profilePhoto) => {
    if (!profilePhoto) return defaultProfileImage;
    return `${MEDIA_URL}${profilePhoto}`;
  };

  // Whether transaction is a payout (debit/cash out)
  const isPayoutTransaction = (transaction) =>
    transaction?.source === "payout" || !!transaction?.payoutRequest;

  // Get user name from transaction (order payment user or payout requested-by)
  const getUserName = (transaction) => {
    if (transaction?.payoutRequest?.requestedBy?.fullName) {
      return transaction.payoutRequest.requestedBy.fullName;
    }
    if (transaction?.metadata?.userId?.fullName) {
      return transaction.metadata.userId.fullName;
    }
    return "user";
  };

  // Get order number from transaction
  const getOrderNumber = (transaction) => {
    if (transaction?.metadata?.orderNumber) {
      return transaction.metadata.orderNumber;
    }
    if (transaction?.metadata?.orderId?.orderNumber) {
      return transaction.metadata.orderId.orderNumber;
    }
    return "-";
  };

  // Get order ID from transaction
  const getOrderId = (transaction) => {
    if (transaction?.metadata?.orderId?._id) {
      return String(transaction.metadata.orderId._id);
    }
    if (transaction?.metadata?.orderId) {
      return String(transaction.metadata.orderId);
    }
    return null;
  };

  // Get transaction ID (wallet transaction id or payout request id for payouts)
  const getTransactionId = (transaction) => {
    if (transaction?.id) return transaction.id;
    if (transaction?._id) return transaction._id;
    if (transaction?.payoutRequest?._id) return String(transaction.payoutRequest._id);
    return "-";
  };

  // Get location from transaction
  const getLocation = (transaction) => {
    if (transaction?.metadata?.location) {
      return transaction.metadata.location;
    }
    if (transaction?.metadata?.locationName) {
      return transaction.metadata.locationName;
    }
    return "-";
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const time = formatTime(dateString);
    const dateStr = formatDate(dateString);
    return `${time}, ${dateStr}`;
  };

  // Selected transaction state
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Bank account dialog state
  const [bankAccountDialogOpen, setBankAccountDialogOpen] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  
  // Cash out dialog state
  const [cashOutDialogOpen, setCashOutDialogOpen] = useState(false);
  const [cashOutAmount, setCashOutAmount] = useState("");
  
  // Cash out result dialog state
  const [cashOutResultDialogOpen, setCashOutResultDialogOpen] = useState(false);
  const [cashOutResult, setCashOutResult] = useState(null); // { success: boolean, transactionId: string, amount: number, errorMessage: string }

  // Delete bank account mutation
  const deleteBankAccountMutation = useMutation(
    async (accountId) => {
      const res = await axiosInstance.delete(ApiEndPoints.BANK_ACCOUNT.delete(accountId));
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("bank account deleted  ");
        queryClient.invalidateQueries(["bankAccounts", locationId]);
        queryClient.invalidateQueries(["walletData", locationId]);
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || "failed to delete bank account";
        toast.error(errorMessage);
      },
    }
  );

  // Cash out mutation
  const cashOutMutation = useMutation(
    async ({ locationId, amount }) => {
      const res = await axiosInstance.post(ApiEndPoints.WALLET.cashOut, {
        locationId,
        amount: parseFloat(amount),
      });
      return res.data;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(["walletData", locationId]);
        queryClient.invalidateQueries(["lastCashOut", locationId]);
        setCashOutDialogOpen(false);
        setCashOutAmount("");
        // Show success dialog
        setCashOutResult({
          success: true,
          transactionId: data?.data?.transactionId || data?.transactionId || `xV${Math.floor(Math.random() * 10000)}-${Math.floor(Math.random() * 10000)}`,
          amount: parseFloat(variables.amount),
          cashoutFeePercentage: walletData?.cashoutFeePercentage,
        });
        setCashOutResultDialogOpen(true);
      },
      onError: (error, variables) => {
        const errorMessage = error?.response?.data?.message || "failed to process cash out request";
        setCashOutDialogOpen(false);
        // Show error dialog
        setCashOutResult({
          success: false,
          transactionId: `xV${Math.floor(Math.random() * 10000)}-${Math.floor(Math.random() * 10000)}`,
          amount: parseFloat(variables.amount),
          errorMessage: errorMessage,
          cashoutFeePercentage: walletData?.cashoutFeePercentage,
        });
        setCashOutResultDialogOpen(true);
      },
    }
  );

  // Handle edit bank account
  const handleEditBankAccount = (account) => {
    setEditingBankAccount(account);
    setBankAccountDialogOpen(true);
  };

  // Handle delete bank account
  const handleDeleteBankAccount = (accountId) => {
    if (window.confirm("are you sure you want to delete this bank account?")) {
      deleteBankAccountMutation.mutate(accountId);
    }
  };

  // Handle open new bank account dialog
  const handleOpenBankAccountDialog = () => {
    setEditingBankAccount(null);
    setBankAccountDialogOpen(true);
  };

  // Handle close bank account dialog
  const handleCloseBankAccountDialog = () => {
    setBankAccountDialogOpen(false);
    setEditingBankAccount(null);
    refetchBankAccounts();
  };

  // Filter transactions based on search
  const filteredTransactions = useMemo(() => {
    if (!walletData?.transactions) return [];
    if (!searchQuery) return walletData.transactions;
    
    const query = searchQuery.toLowerCase();
    return walletData.transactions.filter((transaction) => {
      const userName = getUserName(transaction).toLowerCase();
      const orderNumber = getOrderNumber(transaction).toLowerCase();
      const location = getLocation(transaction).toLowerCase();
      return (
        userName.includes(query) ||
        orderNumber.includes(query) ||
        location.includes(query)
      );
    });
  }, [walletData?.transactions, searchQuery]);

  // Get selected location
  const selectedLocation = useMemo(() => {
    return businessLocationData?.locations?.find(
      (loc) => loc._id === locationId
    );
  }, [businessLocationData, locationId]);

  // Get business name
  const businessName = useMemo(() => {
    return selectedLocation?.businessLocationName || "business name";
  }, [selectedLocation]);

  // Get currency from selected location
  const locationCurrency = useMemo(() => {
    return selectedLocation?.currency || walletData?.currency || "USD";
  }, [selectedLocation, walletData]);

  // Get currency symbol from selected location
  const locationCurrencySymbol = useMemo(() => {
    return selectedLocation?.currencySymbol || "₹";
  }, [selectedLocation]);


  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        width="100%"
        px={2}
      >
        <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
          {error?.response?.data?.message ||
            "failed to load wallet data. please try again."}
        </Alert>
      </Box>
    );
  }

  if (!walletData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        width="100%"
        px={2}
      >
        <Alert severity="info" sx={{ width: "100%", maxWidth: 600 }}>
          no wallet data available for this location.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: IsMaxView 
          ? { xs: "100%", sm: "100%", md: 1200, lg: 1400 }
          : { xs: "100%", sm: "100%", md: 800, lg: 1000 },
        mx: "auto",
        p: IsMaxView 
          ? { xs: 1.5, sm: 2, md: 2.5, lg: 3 }
          : { xs: 1.5, sm: 2, md: 2 },
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          sx={{
            bgcolor: "#34C759",
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: { xs: 24, sm: 28 } }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            sx={{
              fontWeight: 600,
              color: "#222",
              textTransform: "lowercase",
              mb: 0.5,
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
            }}
          >
            wallet setup
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              textTransform: "lowercase",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            view your wallet balance and transaction history
          </Typography>
        </Box>
      </Box>

      {/* Location Dropdown */}
      {businessLocationData?.locations && businessLocationData.locations.length > 0 && (
        <FormControl 
          fullWidth 
          size="small" 
          sx={{ 
            mb: { xs: 1.5, sm: 2 }, 
            maxWidth: { xs: "100%", sm: 320 } 
          }}
        >
          <Select
            disabled={isDisabled}
            value={selectedLocationId || defaultLocationObj?._id || ""}
            onChange={(e) => {
              if (isDisabled) return;
              setSelectedLocationId(e.target.value);
              setSelectedTransaction(null);
            }}
            displayEmpty
            sx={{
              bgcolor: "#fff",
              color: "#7c28d6",
              fontSize: { 
                xs: "0.875rem", 
                sm: IsMaxView ? "1.13em" : "1em" 
              },
              fontWeight: 500,
              borderRadius: 2,
              textTransform: "lowercase",
              border: "1px solid #e0e0e0",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
            renderValue={(value) => {
              if (!value) return `select location(${businessLocationData.locations.length})`;
              const selected = businessLocationData.locations.find(loc => loc._id === value);
              return selected ? `${selected.businessLocationName}(${businessLocationData.locations.length})` : `select location(${businessLocationData.locations.length})`;
            }}
          >
            <MenuItem value="" disabled sx={{ color: "#999" }}>
              select location({businessLocationData.locations.length})
            </MenuItem>
            {businessLocationData.locations.map((loc) => (
              <MenuItem key={loc._id} value={loc._id} sx={{ color: "#7c28d6" }}>
                {loc.businessLocationName}
                {loc.isPrimaryAddress && (
                  <Chip
                    size="small"
                    label="primary"
                    sx={{
                      ml: 1,
                      height: 20,
                      fontSize: "0.7em",
                      bgcolor: "#7c28d6",
                      color: "#fff",
                    }}
                  />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Current Balance Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: { xs: 1.5, sm: 2 },
          bgcolor: "rgba(242, 233, 255, 1)",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: { xs: 1.5, sm: 2 },
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  color: "#222",
                  textTransform: "lowercase",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                {businessName}
              </Typography>
              <StarIcon sx={{ color: "#7c28d6", fontSize: { xs: 16, sm: 18 } }} />
              <ApprovIcon
                color={businessLocationData?.business?.isApprove === 1 && businessLocationData?.business?.isDocumentVerified === 1 ? "#0d6efd" : "#ffc107"}
                size={16} 
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                mb: 0.5,
                textTransform: "lowercase",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              current balance
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#222",
                mb: 0.5,
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
              }}
            >
              {range === "all-time"
                ? formatCurrency(walletData.balance, locationCurrency)
                : formatCurrency(rangeBalance, locationCurrency)
              }
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                textTransform: "lowercase",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              {locationCurrency?.toLowerCase() || "usd"}
            </Typography>
          </Box>
          <Box sx={{ 
            display: "flex", 
            gap: 1.5, 
            alignItems: "flex-start",
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
          }}>
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 120 } }}>
              <Select
                disabled={isDisabled}
                value={range}
                onChange={handleFilterChange}
                defaultValue="last week"
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 1,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  textTransform: "lowercase",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #e0e0e0",
                  },
                }}
              >
                <MenuItem value="last-week" sx={{ textTransform: "lowercase" }}>
                  last week
                </MenuItem>
                <MenuItem value="last-month" sx={{ textTransform: "lowercase" }}>
                  last month
                </MenuItem>
                <MenuItem value="last-year" sx={{ textTransform: "lowercase" }}>
                  last year
                </MenuItem>
                <MenuItem value="all-time" sx={{ textTransform: "lowercase" }}>
                  all time
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ 
          display: "flex", 
          gap: { xs: 1, sm: 1.5 },
          flexDirection: { xs: "column", sm: "row" },
        }}>
          <Button
            variant="contained"
            fullWidth={isMobile}
            disabled={
              isDisabled || 
              !hasBankAccounts || 
              businessLocationData?.business?.isApprove !== 1 || 
              businessLocationData?.business?.isDocumentVerified !== 1 ||
              !bankAccountsData?.accounts[0]?.isVerified ||
              hasSuspendedCashOut
            }
            onClick={() => setCashOutDialogOpen(true)}
            sx={{
              bgcolor: "#7c28d6",
              color: "#fff",
              textTransform: "lowercase",
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: 1.5,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              "&:hover": {
                bgcolor: "#6a1fb8",
              },
            }}
          >
            cash out
          </Button>
        </Box>
        {walletData?.cashoutFeePercentage > 0 && (
          <Typography
            variant="caption"
            sx={{
              mt: 1.5,
              color: "#666",
              textTransform: "lowercase",
              display: "block",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            when you cashout then {walletData.cashoutFeePercentage}% will be cut as a fee
          </Typography>
        )}
        
        {/* Last Cash Out Status Message */}
        {lastCashOutStatus && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: lastCashOutStatus.status === "approve" 
                  ? "#34C759" 
                  : lastCashOutStatus.status === "pending"
                  ? "#f57c00"
                  : "#d32f2f",
                textTransform: "lowercase",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                fontWeight: 500,
              }}
            >
              {lastCashOutStatus.status === "pending"
                ? "your last cash req is in pending wait for approval"
                : lastCashOutStatus.status === "approve" 
                ? "your last cash-out has been approved and will be deposited within 7–10 working days."
                : lastCashOutStatus.status === "reject"
                ? `your last cash-out request was rejected. ${lastCashOutStatus.reason ? `reason: ${lastCashOutStatus.reason}` : "please try again"}`
                : lastCashOutStatus.status === "suspend"
                ? `your last cash-out request was suspended. ${lastCashOutStatus.reason ? `reason: ${lastCashOutStatus.reason}` : "please contact support"}`
                : ""}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Linked Bank Accounts */}
      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
        {/* Display existing bank accounts */}
        {bankAccountsData?.accounts && bankAccountsData.accounts.length > 0 && (
          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
            {bankAccountsData.accounts.map((account) => (
              <Box
                key={account.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: { xs: 1, sm: 1.5 },
                  px: { xs: 0.5, sm: 1 },
                  flexWrap: "wrap",
                  gap: { xs: 0.5, sm: 1 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#222",
                    textTransform: "lowercase",
                    fontSize: "1rem",
                  }}
                >
                  linked bank account
                </Typography>

                <Box sx={{ display: "flex", gap: 0.5, width: "100%" }}>
                  <Box sx={{
                    width: "80%",
                    alignItems: "center",
                    justifyContent: "start",
                    display:"flex"
                  }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {account.bankName || "bank"} ending in {account.accountNumber || "0000"} 
                    </Typography>
                      &nbsp;
                    {account?.isVerified && (
                      <CheckCircleIcon
                        color="#0d6efd"
                        size={16}
                        sx={{ color: "#34C759", fontSize: 16 }}
                      />
                    )}
                  </Box>

                  <Box sx={{
                    width: "20%",
                    display: "flex",
                    alignItems:"center",
                    justifyContent: "end"
                  }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (isDisabled) return;
                        handleEditBankAccount(account);
                      }}
                      disabled={isDisabled || deleteBankAccountMutation.isLoading}
                      sx={{
                        color: "#7c28d6",
                        p: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <EditIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (isDisabled) return;
                        handleDeleteBankAccount(account.id);
                      }}
                      disabled={isDisabled || deleteBankAccountMutation.isLoading}
                      sx={{
                        color: "#d32f2f",
                        p: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Box>
                </Box>

                {account?.isVerified === false && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#ffc107",
                      textTransform: "lowercase",
                      fontSize: "0.75rem",
                      fontStyle: "italic",
                    }}
                  >
                    your bank details are awaiting for admin approval. Please wait for verification.
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Link New Bank Account Section */}
        {bankAccountsData?.accounts && bankAccountsData.accounts.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              border: "2px solid #7c28d6",
              borderRadius: 2,
              bgcolor: "rgba(242, 233, 255, 0.3)",
            }}
          >
            <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
              <Typography
                variant="body1"
                sx={{
                  color: "#222",
                  textTransform: "lowercase",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  mb: { xs: 1.5, sm: 2 },
                }}
              >
                link new bank account?
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  if (isDisabled) return;
                  handleOpenBankAccountDialog();
                }}
                disabled={isDisabled}
                sx={{
                  bgcolor: "#7c28d6",
                  color: "#fff",
                  textTransform: "lowercase",
                  px: { xs: 2, sm: 3 },
                  py: { xs: 0.75, sm: 1 },
                  borderRadius: 1.5,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  width: "100%",
                  "&:hover": {
                    bgcolor: "#6a1fb8",
                  },
                }}
              >
                link
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Transaction History Section */}
      <Box sx={{ 
        display: "flex", 
        gap: { xs: 1.5, sm: 2, md: 2.5 }, 
        flexDirection: { xs: "column", md: IsMaxView ? "column" : "row" },
        alignItems: { xs: "stretch", md: "flex-start" },
        mt: { xs: 0, sm: 0.5 },
      }}>
        {/* Transaction List */}
        <Box sx={{ 
          flex: { xs: 1, md: IsMaxView ? "0 0 60%" : "1 1 100%" },
          minWidth: 0,
          width: "100%",
        }}>
          <Box sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: 'flex-start',
            mb: { xs: 1.5, sm: 2 },
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2 },
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#222",
                textTransform: "lowercase",
                fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
              }}
            >
              transaction history for '{businessName}'
            </Typography>
            
            {/* Search and Calendar */}
            <Box sx={{ 
              display: "flex", 
              gap: 1, 
              width: "100%",
              alignItems: "center",
            }}>
              <TextField
                size="small"
                placeholder="search"
                value={searchQuery}
                onChange={(e) => {
                  if (isDisabled) return;
                  setSearchQuery(e.target.value);
                }}
                disabled={isDisabled}
                sx={{
                  flex: { xs: 1, sm: "none" },
                  minWidth: "90%",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    bgcolor: "#fff",
                    fontSize: { xs: "0.875rem", sm: "0.875rem" },
                    height: { xs: 36, sm: 40 },
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#999", fontSize: { xs: "1rem", sm: "1.2rem" } }} />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                size="small"
                disabled={isDisabled}
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1.5,
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  color: "#7c28d6",
                  "&:hover": {
                    bgcolor: "rgba(124, 40, 214, 0.1)",
                  },
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }} />
              </IconButton>
            </Box>
          </Box>

          {filteredTransactions && filteredTransactions.length > 0 ? (
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: 2,
                maxHeight: { xs: "none", sm: 500, md: 600 },
                overflowY: { xs: "visible", sm: "auto" },
                overflowX: "hidden",
                border: "1px solid #e0e0e0",
                "&::-webkit-scrollbar": {
                  width: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "#7c28d6",
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-track": {
                  bgcolor: "transparent",
                },
              }}
            >
              <Stack spacing={0} sx={{ p: { xs: 0.5, sm: 1 } }}>
                {filteredTransactions.map((transaction) => {
                  const userName = getUserName(transaction);
                  const orderId = getOrderId(transaction);
                  const profilePhoto =
                    transaction?.payoutRequest?.requestedBy?.profilePhoto ??
                    transaction?.metadata?.userId?.profilePhoto ??
                    null;
                  const location = getLocation(transaction);
                  const isSelected = selectedTransaction?.id === transaction.id;

                  return (
                    <Paper
                      key={transaction.id}
                      elevation={0}
                      onClick={() => {
                        if (isDisabled) return;
                        setSelectedTransaction(transaction);
                        setDialogOpen(true);
                      }}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: { xs: 1, sm: 0.5 },
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                        bgcolor: isSelected ? "rgba(242, 233, 255, 1)" : "transparent",
                        borderLeft: isSelected ? "3px solid #7c28d6" : "3px solid transparent",
                        borderRadius: 1.5,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: isSelected ? "rgba(242, 233, 255, 1)" : "rgba(242, 233, 255, 0.5)",
                          borderLeft: "3px solid #7c28d6",
                        },
                        "&:last-child": {
                          mb: 0,
                        },
                      }}
                    >
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: { xs: "flex-start", sm: "center" }, 
                        gap: { xs: 1.5, sm: 2 },
                        flexDirection: { xs: "column", sm: "row" },
                      }}>
                        {/* Avatar and Name */}
                        <Box sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: { xs: 1, sm: 1.5 },
                          flex: { xs: "1 1 100%", sm: "1 1 auto" },
                          minWidth: 0,
                          width: { xs: "100%", sm: "auto" },
                        }}>
                          <Avatar
                            src={getProfileImageUrl(profilePhoto)}
                            alt={userName}
                            sx={{
                              width: { xs: 40, sm: 48 },
                              height: { xs: 40, sm: 48 },
                              bgcolor: "#E0E0E0",
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              e.target.src = defaultProfileImage;
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#222",
                                textTransform: "lowercase",
                                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                                mb: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {userName}
                            </Typography>
                            {orderId && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#7c28d6",
                                  textTransform: "lowercase",
                                  cursor: "pointer",
                                  display: "block",
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  "&:hover": {
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                order id #{orderId.length > (isMobile ? 18 : 25) ? orderId.substring(0, isMobile ? 18 : 25) + "..." : orderId}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Transaction Amount */}
                        <Box sx={{ 
                          flex: { xs: "0 0 auto", sm: "0 0 120px" },
                          textAlign: { xs: "left", sm: "right" },
                          alignSelf: { xs: "flex-start", sm: "center" },
                          width: { xs: "100%", sm: "auto" },
                        }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#222",
                              fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                            }}
                          >
                            {formatCurrency(transaction.amount, locationCurrency)}
                          </Typography>
                        </Box>

                        {/* Location and Time */}
                        <Box sx={{ 
                          flex: { xs: "1 1 100%", sm: "0 0 180px" },
                          textAlign: { xs: "left", sm: "right" },
                          minWidth: 0,
                          width: { xs: "100%", sm: "auto" },
                        }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#666",
                              textTransform: "lowercase",
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              mb: 0.25,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {location}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#999",
                              fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            }}
                          >
                            {formatDateTime(transaction.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: "#fff",
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  textTransform: "lowercase",
                }}
              >
                no transactions found
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Transaction Details Panel - Only show in Max View */}
        {selectedTransaction && IsMaxView && (
          <Box sx={{ 
            flex: { xs: 1, md: "0 0 35%" },
            minWidth: 0,
            width: "100%",
          }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                bgcolor: "#fff",
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                position: { xs: "relative", md: "sticky" },
                top: { md: 20 },
                mb: { xs: 2, md: 0 },
                maxHeight: { xs: "none", md: "calc(100vh - 40px)" },
                overflowY: { xs: "visible", md: "auto" },
                "&::-webkit-scrollbar": {
                  width: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "#7c28d6",
                  borderRadius: 4,
                },
              }}
            >
              <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: { xs: 1.5, sm: 2 }, 
                  mb: { xs: 1.5, sm: 2 } 
                }}>
                  <Avatar
                    src={getProfileImageUrl(
                      selectedTransaction?.payoutRequest?.requestedBy?.profilePhoto ??
                      selectedTransaction?.metadata?.userId?.profilePhoto
                    )}
                    alt={getUserName(selectedTransaction)}
                    sx={{
                      width: { xs: 50, sm: 60 },
                      height: { xs: 50, sm: 60 },
                      bgcolor: "#E0E0E0",
                      flexShrink: 0,
                    }}
                    onError={(e) => {
                      e.target.src = defaultProfileImage;
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#222",
                        textTransform: "lowercase",
                        mb: 0.5,
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    >
                      {getUserName(selectedTransaction)}
                    </Typography>
                    <Box sx={{ 
                      display: "flex", 
                      gap: { xs: 0.5, sm: 1 }, 
                      alignItems: "center", 
                      flexWrap: "wrap" 
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          textTransform: "lowercase",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {getLocation(selectedTransaction)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#999",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {formatDateTime(selectedTransaction.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 1.5 } }}>
                {getOrderId(selectedTransaction) && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                    >
                      order id
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#7c28d6",
                        textTransform: "lowercase",
                        cursor: "pointer",
                        wordBreak: "break-all",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      #{getOrderId(selectedTransaction)}
                    </Typography>
                  </Box>
                )}
                {getOrderNumber(selectedTransaction) !== "-" && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                    >
                      order number
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#7c28d6",
                        textTransform: "lowercase",
                        cursor: "pointer",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      #{getOrderNumber(selectedTransaction)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      textTransform: "lowercase",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  >
                    transaction id
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#222",
                      textTransform: "lowercase",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      wordBreak: "break-all",
                    }}
                  >
                    {getTransactionId(selectedTransaction)}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      textTransform: "lowercase",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  >
                    {isPayoutTransaction(selectedTransaction) ? "payout details" : "payment made in app"}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

              <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                {isPayoutTransaction(selectedTransaction) && selectedTransaction?.payoutRequest ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      requested amount: {formatCurrency((selectedTransaction.payoutRequest.amount || 0) * 100, locationCurrency)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      cashout fee ({walletData?.cashoutFeePercentage ?? 0}%): {formatCurrency((selectedTransaction.payoutRequest.cashoutFee || 0) * 100, locationCurrency)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#222",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        mt: 1,
                        mb: 0.5,
                      }}
                    >
                      paid amount: {formatCurrency((selectedTransaction.payoutRequest.paidAmount || 0) * 100, locationCurrency)}
                    </Typography>
                    {selectedTransaction.payoutRequest.bankAccount && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          textTransform: "lowercase",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          mt: 0.5,
                        }}
                      >
                        bank: {selectedTransaction.payoutRequest.bankAccount.bankName} ****{selectedTransaction.payoutRequest.bankAccount.last4}
                      </Typography>
                    )}
                    {selectedTransaction.payoutRequest.processedAt && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#999",
                          textTransform: "lowercase",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          mt: 0.5,
                        }}
                      >
                        processed: {formatDateTime(selectedTransaction.payoutRequest.processedAt)}
                      </Typography>
                    )}
                  </>
                ) : selectedTransaction?.metadata?.handlingFee != null ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      total amount: {formatCurrency(selectedTransaction.metadata.baseAmount * 100, locationCurrency)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      handling fee ({selectedTransaction.metadata.handlingFeePercentage}%): {formatCurrency(selectedTransaction.metadata.handlingFee * 100, locationCurrency)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#222",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        mt: 1,
                      }}
                    >
                      net amount: {formatCurrency(selectedTransaction.metadata.netAmount * 100, locationCurrency)}
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#222",
                      textTransform: "lowercase",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    total amount: {formatCurrency(selectedTransaction.amount, locationCurrency)}
                  </Typography>
                )}
              </Box>

              {/**  temporary commented out, can be used in future : comment out date: 09012026 */}
              {/* <Box sx={{ 
                display: "flex", 
                gap: { xs: 1, sm: 1.5 }, 
                flexDirection: { xs: "column", md: IsMaxView ? "row" : "column", lg: "row" },
              }}>
                <Button
                  variant="contained"
                  fullWidth={isMobile || (!IsMaxView && isTablet)}
                  disabled={isDisabled}
                  sx={{
                    bgcolor: "#1976d2",
                    color: "#fff",
                    textTransform: "lowercase",
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    flex: { xs: "1 1 100%", md: IsMaxView ? "1 1 auto" : "1 1 100%", lg: "1 1 auto" },
                    minWidth: { md: IsMaxView ? 100 : "auto", lg: 100 },
                    "&:hover": {
                      bgcolor: "#1565c0",
                    },
                  }}
                >
                  refund
                </Button>
                <Button
                  variant="contained"
                  fullWidth={isMobile || (!IsMaxView && isTablet)}
                  disabled={isDisabled}
                  sx={{
                    bgcolor: "#d32f2f",
                    color: "#fff",
                    textTransform: "lowercase",
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    flex: { xs: "1 1 100%", md: IsMaxView ? "1 1 auto" : "1 1 100%", lg: "1 1 auto" },
                    minWidth: { md: IsMaxView ? 100 : "auto", lg: 100 },
                    "&:hover": {
                      bgcolor: "#c62828",
                    },
                  }}
                >
                  cancel
                </Button>
                <Button
                  variant="contained"
                  fullWidth={isMobile || (!IsMaxView && isTablet)}
                  disabled={isDisabled}
                  sx={{
                    bgcolor: "#f57c00",
                    color: "#fff",
                    textTransform: "lowercase",
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    flex: { xs: "1 1 100%", md: IsMaxView ? "1 1 auto" : "1 1 100%", lg: "1 1 auto" },
                    minWidth: { md: IsMaxView ? 100 : "auto", lg: 100 },
                    "&:hover": {
                      bgcolor: "#e65100",
                    },
                  }}
                >
                  notes
                </Button>
              </Box> */}
            </Paper>
          </Box>
        )}
      </Box>

      {/* disclaimer Card */}
      <Box sx={{ 
        display: "flex", 
        gap: { xs: 1.5, sm: 2, md: 2.5 }, 
        flexDirection: { xs: "column", md: IsMaxView ? "column" : "row" },
        alignItems: { xs: "stretch", md: "flex-start" },
        mt: { xs: 0, sm: 0.5 },
      }}>
        <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: "#fff",
                textAlign: "left",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  textTransform: "lowercase",
                }}
              >
                disclaimer: the beemz app or its stakeholders are not liable for grievances caused by entering incorrect information. triple-check your bank details as you enter them. 
              </Typography>
            </Paper>
      </Box>

      {/* Transaction Details Dialog */}
      <Dialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedTransaction(null);
          }}
          maxWidth={false}
          PaperProps={{
            sx: {
              borderRadius: 2,
              width: { xs: "90%", sm: "70%", md: "50%", lg: "40%" },
              maxWidth: { xs: "90%", sm: "500px", md: "450px", lg: "400px" },
              m: { xs: 2, sm: 3 },
            },
          }}
        >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#222",
              textTransform: "lowercase",
            }}
          >
            transaction details
          </Typography>
          <IconButton
            onClick={() => {
              setDialogOpen(false);
              setSelectedTransaction(null);
            }}
            size="small"
            sx={{ color: "#666" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransaction && (
            <>
              <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: { xs: 1.5, sm: 2 }, 
                  mb: { xs: 1.5, sm: 2 } 
                }}>
                  <Avatar
                    src={getProfileImageUrl(
                      selectedTransaction?.payoutRequest?.requestedBy?.profilePhoto ??
                      selectedTransaction?.metadata?.userId?.profilePhoto
                    )}
                    alt={getUserName(selectedTransaction)}
                    sx={{
                      width: { xs: 50, sm: 60 },
                      height: { xs: 50, sm: 60 },
                      bgcolor: "#E0E0E0",
                      flexShrink: 0,
                    }}
                    onError={(e) => {
                      e.target.src = defaultProfileImage;
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#222",
                        textTransform: "lowercase",
                        mb: 0.5,
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    >
                      {getUserName(selectedTransaction)}
                    </Typography>
                    <Box sx={{ 
                      display: "flex", 
                      gap: { xs: 0.5, sm: 1 }, 
                      alignItems: "center", 
                      flexWrap: "wrap" 
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          textTransform: "lowercase",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {getLocation(selectedTransaction)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#999",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {formatDateTime(selectedTransaction.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 1.5 } }}>
                {getOrderId(selectedTransaction) && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                    >
                      order id
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#7c28d6",
                        textTransform: "lowercase",
                        cursor: "pointer",
                        wordBreak: "break-all",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      #{getOrderId(selectedTransaction)}
                    </Typography>
                  </Box>
                )}
                {getOrderNumber(selectedTransaction) !== "-" && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                    >
                      order number
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#7c28d6",
                        textTransform: "lowercase",
                        cursor: "pointer",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      #{getOrderNumber(selectedTransaction)}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      textTransform: "lowercase",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  >
                    transaction id
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#222",
                      textTransform: "lowercase",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      wordBreak: "break-all",
                    }}
                  >
                    {getTransactionId(selectedTransaction)}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      textTransform: "lowercase",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  >
                    {isPayoutTransaction(selectedTransaction) ? "payout details" : "payment made in app"}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

              <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                {isPayoutTransaction(selectedTransaction) && selectedTransaction?.payoutRequest ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      requested amount: {formatCurrency((selectedTransaction.payoutRequest.amount || 0) * 100, locationCurrency)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      cashout fee ({walletData?.cashoutFeePercentage ?? 0}%): {formatCurrency((selectedTransaction.payoutRequest.cashoutFee || 0) * 100, locationCurrency)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#222",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        mt: 1,
                        mb: 0.5,
                      }}
                    >
                      paid amount: {formatCurrency((selectedTransaction.payoutRequest.paidAmount || 0) * 100, locationCurrency)}
                    </Typography>
                    {selectedTransaction.payoutRequest.bankAccount && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          textTransform: "lowercase",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          mt: 0.5,
                        }}
                      >
                        bank: {selectedTransaction.payoutRequest.bankAccount.bankName} ****{selectedTransaction.payoutRequest.bankAccount.last4}
                      </Typography>
                    )}
                    {selectedTransaction.payoutRequest.processedAt && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#999",
                          textTransform: "lowercase",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          mt: 0.5,
                        }}
                      >
                        processed: {formatDateTime(selectedTransaction.payoutRequest.processedAt)}
                      </Typography>
                    )}
                  </>
                ) : selectedTransaction?.metadata?.handlingFee != null ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      total amount: {formatCurrency(selectedTransaction.metadata.baseAmount * 100, locationCurrency)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        mb: 0.5,
                      }}
                    >
                      handling fee ({selectedTransaction.metadata.handlingFeePercentage}%): {formatCurrency(selectedTransaction.metadata.handlingFee * 100, locationCurrency)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#222",
                        textTransform: "lowercase",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        mt: 1,
                      }}
                    >
                      net amount: {formatCurrency(selectedTransaction.metadata.netAmount * 100, locationCurrency)}
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#222",
                      textTransform: "lowercase",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    total amount: {formatCurrency(selectedTransaction.amount, locationCurrency)}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        
        {/**  temporary commented out, can be used in future : comment out date: 09012026 */}
        {/* <DialogActions sx={{ 
          p: { xs: 2, sm: 2.5 }, 
          pt: 2, 
          gap: 1.5,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
        }}>
          <Button
            variant="contained"
            fullWidth={isMobile}
            disabled={isDisabled}
            sx={{
              bgcolor: "#1976d2",
              color: "#fff",
              textTransform: "lowercase",
              py: { xs: 0.75, sm: 1 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              minWidth: { sm: 100 },
              "&:hover": {
                bgcolor: "#1565c0",
              },
            }}
          >
            refund
          </Button>
          <Button
            variant="contained"
            fullWidth={isMobile}
            disabled={isDisabled}
            sx={{
              bgcolor: "#d32f2f",
              color: "#fff",
              textTransform: "lowercase",
              py: { xs: 0.75, sm: 1 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              minWidth: { sm: 100 },
              "&:hover": {
                bgcolor: "#c62828",
              },
            }}
          >
            cancel
          </Button>
          <Button
            variant="contained"
            fullWidth={isMobile}
            disabled={isDisabled}
            sx={{
              bgcolor: "#f57c00",
              color: "#fff",
              textTransform: "lowercase",
              py: { xs: 0.75, sm: 1 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              minWidth: { sm: 100 },
              "&:hover": {
                bgcolor: "#e65100",
              },
            }}
          >
            notes
          </Button>
        </DialogActions> */}
        </Dialog>

      {/* Cash Out Dialog */}
      <CashOutDialog
        open={cashOutDialogOpen}
        onClose={() => {
          setCashOutDialogOpen(false);
          setCashOutAmount("");
        }}
        cashOutAmount={cashOutAmount}
        onCashOutAmountChange={(e) => setCashOutAmount(e.target.value)}
        businessName={businessName}
        walletData={walletData}
        locationCurrency={locationCurrency}
        locationCurrencySymbol={locationCurrencySymbol}
        formatCurrency={formatCurrency}
        isDisabled={isDisabled}
        isLoading={cashOutMutation.isLoading}
        locationId={locationId}
        lastCashOutDate={lastCashOutDate}
        onCashOut={() => {
          if (!locationId || !cashOutAmount) return;
          cashOutMutation.mutate({
            locationId,
            amount: cashOutAmount,
          });
        }}
      />

      {/* Cash Out Result Dialog */}
      <CashOutResultDialog
        open={cashOutResultDialogOpen}
        onClose={() => {
          setCashOutResultDialogOpen(false);
          setCashOutResult(null);
        }}
        result={cashOutResult}
        formatCurrency={formatCurrency}
        locationCurrency={locationCurrency}
      />

      {/* Bank Account Dialog */}
      <BankAccountDialog
        open={bankAccountDialogOpen}
        onClose={handleCloseBankAccountDialog}
        businessLocationData={businessLocationData}
        defaultLocationObj={defaultLocationObj}
        locationId={locationId}
        countryData={countryData}
        currencyData={currencyData}
        walletCurrency={locationCurrency}
        editingAccount={editingBankAccount}
        selectedLocation={selectedLocation}
      />
    </Box>
  );
};

export default WalletSetup;

